/**
 * The member dashboard. Where a free member lands after logging in (Nov 2026).
 *
 * Order is the point. The 12 Days of Claudmas leads, because that is what the
 * free membership is selling right now. The next live, clips and free
 * resources sit under it as honest "coming soon" slots: no lives are scheduled
 * yet, and an empty slot that says so beats a date nobody has picked. The AI
 * tools and the Roles Map are still one click away at the bottom, in the
 * background rather than first.
 *
 * Unlocking and downloads are decided by the server (routers/members.ts); this
 * page only shows what it is told. A locked day's video id never reaches the
 * browser.
 *
 * Deliberately no community or member feed. Not decided yet.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { CalendarClock, Check, Download, PlayCircle } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { BUTTON } from "@/lib/buttonStyles";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";
import { useOpenVideoPlayer } from "@/hooks/useOpenVideoPlayer";
import { unlockLabel } from "@shared/claudmas";

const SERIF = "font-['Fraunces'] font-semibold text-foreground tracking-tight";

/** The 12 Days palette. Same five values as yoursmartstack.com's
    assets/css/site.css (--xm-*); change them together. */
const XM = {
  red: "#B01E2A",
  pine: "#1D4A33",
  honey: "#E2B23F",
  paper: "#FFFDF8",
  bone: "#F8EFE0",
  ink: "#2A1414",
  line: "#E7D6BD",
  slate: "#6A4F45",
} as const;
const EYEBROW = "text-xs font-semibold uppercase tracking-widest text-primary";

/**
 * Placeholder: where "Need help setting this up?" goes. Undecided — on Oct 26
 * the community was to wait for about 50 members; on Oct 30 a community
 * section for setup help came up. Until that is answered the button is shown
 * disabled with a note, and wiring it is one string here.
 */
const SETUP_HELP_URL = "";

function Video({ videoId, title }: { videoId: string; title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useOpenVideoPlayer(ref, videoId, { enabled: Boolean(videoId) });
  if (!videoId) {
    return (
      <div className="aspect-video w-full rounded-xl border border-dashed border-border bg-muted/40 flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <PlayCircle className="h-10 w-10" aria-hidden="true" />
        <span className="text-base">Video coming soon</span>
      </div>
    );
  }
  return <div ref={ref} className="aspect-video w-full rounded-xl overflow-hidden bg-black" aria-label={title} />;
}

function ComingSoon({ eyebrow, title, body, video }: { eyebrow: string; title: string; body: string; video?: boolean }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
      <p className={EYEBROW}>{eyebrow}</p>
      <h2 className={`mt-2 text-2xl ${SERIF}`}>{title}</h2>
      <p className="mt-2 text-base text-muted-foreground">{body}</p>
      {video ? <div className="mt-4"><Video videoId="" title={title} /></div> : null}
      <span className="mt-4 inline-flex rounded-full bg-muted px-3 py-1 text-sm font-semibold text-muted-foreground">Coming soon</span>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const days = trpc.claudmas.days.useQuery();
  const mine = trpc.claudmas.mine.useQuery();
  const join = trpc.claudmas.join.useMutation({ onSuccess: () => utils.claudmas.mine.invalidate() });
  const optIn = trpc.claudmas.optIn.useMutation({ onSuccess: () => utils.claudmas.mine.invalidate() });
  const [busyDay, setBusyDay] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");

  useEffect(() => { document.title = "Your dashboard | Your Smart Stack"; }, []);

  const list = days.data ?? [];
  const unlocked = list.filter(d => d.unlocked);
  const latest = unlocked[unlocked.length - 1];
  const nextLocked = list.find(d => !d.unlocked);
  const optedIn = new Set(mine.data?.days ?? []);

  const getSkill = async (day: number) => {
    setBusyDay(day);
    setNote("");
    try {
      const res = await optIn.mutateAsync({ day });
      if (res.skillUrl) window.location.href = res.skillUrl;
      else setNote(`You're in for Day ${day}. The skill file will be here as soon as it's ready.`);
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusyDay(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="Dashboard" />
      <main id="main" className="flex-1">
        <div className="container max-w-4xl py-8 sm:py-12 space-y-10">
          {/* ── 12 DAYS OF CLAUDMAS ───────────────────────────────────────
              The campaign's own palette (XM below), mirrored from
              yoursmartstack.com's site.css (.xm-*). Only this block is
              themed: the header above and the sections below stay Your
              Smart Stack, so it reads as a season of the brand. */}
          <section aria-labelledby="claudmas-h" className="space-y-6">
            <div
              className="relative overflow-hidden rounded-xl text-white px-6 py-8 sm:px-10 sm:py-10"
              style={{
                backgroundColor: XM.red,
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.07) 1px,transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            >
              <span
                aria-hidden="true"
                className="absolute top-0 bottom-0 right-5 sm:right-[9%] w-2 sm:w-4"
                style={{ backgroundColor: XM.honey, opacity: 0.9 }}
              />
              <div className="relative pr-8 sm:pr-[14%]">
                <span
                  className="inline-block rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-wider"
                  style={{ backgroundColor: XM.honey, color: XM.ink }}
                >
                  Free &middot; Nov 30 to Dec 11
                </span>
                <h1 id="claudmas-h" className="mt-4 font-['Fraunces'] font-semibold text-4xl sm:text-6xl leading-[.95] tracking-tight text-white">
                  12 Days of Claudmas
                </h1>
                <p className="mt-4 text-lg text-white/90 max-w-xl">
                  {user?.firstName ? `Welcome, ${user.firstName}. ` : ""}
                  {unlocked.length
                    ? `${unlocked.length} of 12 days are open. Grab today's skill below.`
                    : `Day 1 opens ${unlockLabel(1)}. One ready-made skill a day, each one a task off your plate.`}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {mine.data && !mine.data.joined ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 min-h-12 px-5 rounded-md font-semibold text-base"
                      style={{ backgroundColor: XM.honey, color: XM.ink }}
                      onClick={() => join.mutate()}
                      disabled={join.isPending}
                    >
                      Count me in
                    </button>
                  ) : mine.data?.joined ? (
                    <span className="text-base text-white/90">You're signed up. We'll email you as each day opens.</span>
                  ) : null}
                </div>
              </div>
            </div>

            {latest ? (
              <div className="rounded-xl border p-5 sm:p-6" style={{ backgroundColor: XM.paper, borderColor: XM.pine, boxShadow: `inset 4px 0 0 ${XM.pine}` }}>
                <p className="text-sm font-semibold" style={{ color: XM.pine }}>Today: Day {latest.day}</p>
                <h2 className="mt-1 font-['Fraunces'] text-2xl font-semibold" style={{ color: XM.ink }}>{latest.title}</h2>
                <p className="mt-1 text-base" style={{ color: XM.slate }}>{latest.description}</p>
                <div className="mt-4"><Video videoId={latest.videoId} title={latest.title} /></div>
              </div>
            ) : (
              <div className="rounded-xl border p-5 sm:p-6 flex items-center gap-4" style={{ backgroundColor: XM.bone, borderColor: XM.line }}>
                <CalendarClock className="h-8 w-8 shrink-0" style={{ color: XM.red }} aria-hidden="true" />
                <p className="text-base" style={{ color: XM.ink }}>
                  The first skill and its video open {unlockLabel(1)}. Check back then, or watch your inbox.
                </p>
              </div>
            )}

            {note ? <p role="status" className="rounded-lg bg-muted px-4 py-3 text-base text-foreground">{note}</p> : null}

            {/* The calendar: advent doors. Locked doors are bone with a big
                numeral; open doors turn paper with a pine edge. */}
            <ol className="grid gap-3 grid-cols-2 lg:grid-cols-3">
              {list.map(d => {
                const got = optedIn.has(d.day);
                return (
                  <li
                    key={d.day}
                    className="flex flex-col gap-2 rounded-md border p-4"
                    style={
                      d.unlocked
                        ? { backgroundColor: XM.paper, borderColor: XM.pine, boxShadow: `inset 4px 0 0 ${XM.pine}` }
                        : { backgroundColor: XM.bone, borderColor: XM.line }
                    }
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-['Fraunces'] font-semibold text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: XM.red }}>
                        {d.day}
                      </span>
                      <span className="text-sm font-semibold" style={{ color: XM.slate }}>{unlockLabel(d.day)}</span>
                    </div>
                    <h3 className="font-['Fraunces'] text-lg font-semibold leading-snug" style={{ color: XM.ink }}>
                      {d.unlocked ? d.title : "Still wrapped"}
                    </h3>
                    {d.unlocked ? (
                      <>
                        <p className="text-sm" style={{ color: XM.slate }}>{d.description}</p>
                        <button
                          type="button"
                          className={`${BUTTON.primarySm} mt-auto w-full`}
                          onClick={() => getSkill(d.day)}
                          disabled={busyDay === d.day}
                        >
                          {got ? <Check className="h-4 w-4" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
                          {got ? "Download again" : "Get this skill"}
                        </button>
                        <button
                          type="button"
                          className="min-h-11 text-sm underline disabled:cursor-not-allowed"
                          style={{ color: XM.slate }}
                          disabled={!SETUP_HELP_URL}
                          title={SETUP_HELP_URL ? undefined : "Setup help is coming soon"}
                          onClick={() => SETUP_HELP_URL && (window.location.href = SETUP_HELP_URL)}
                        >
                          Need help setting this up?
                        </button>
                      </>
                    ) : (
                      <p className="mt-auto flex items-center gap-2 text-sm font-semibold" style={{ color: XM.slate }}>
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: XM.red }} aria-hidden="true" />
                        Opens at midnight
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
            {nextLocked && unlocked.length ? (
              <p className="text-base text-muted-foreground">Next up: Day {nextLocked.day}, {unlockLabel(nextLocked.day)}.</p>
            ) : null}

            <div>
              <h3 className={`text-xl ${SERIF}`}>Your skills</h3>
              {optedIn.size ? (
                <ul className="mt-2 flex flex-wrap gap-2">
                  {Array.from(optedIn).map(n => (
                    <li key={n} className="rounded-full px-3 py-1 text-sm font-semibold text-white" style={{ backgroundColor: XM.pine }}>Day {n}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-base text-muted-foreground">Skills you grab will show up here.</p>
              )}
            </div>
          </section>

          {/* ── COMING SOON ─────────────────────────────────────────────── */}
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <ComingSoon eyebrow="Next live" title="Live training" body="Free live trainings for members are on the way. When one is scheduled, the date and the stream will be right here." video />
            </div>
            <ComingSoon eyebrow="From the last live" title="Clips" body="Short clips from each live, so you can catch the best parts in a few minutes." />
            <ComingSoon eyebrow="Free resources" title="Resources" body="Checklists, templates and quick-start guides to help you get set up faster." />
          </div>

          {/* ── IN THE BACKGROUND ───────────────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-muted/30 p-5 sm:p-6">
            <h2 className={`text-xl ${SERIF}`}>Your AI tools</h2>
            <p className="mt-1 text-base text-muted-foreground">Everything you had before is still here.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link href="/" className={BUTTON.secondarySm}>AI Roles Map</Link>
              <Link href="/my-stack" className={BUTTON.secondarySm}>My Stack</Link>
              <Link href="/resources" className={BUTTON.secondarySm}>Resource Center</Link>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
